// События идентичности: SET (RFC 8417, типы RISC/CAEP) наружу, Socket.IO — в
// открытую вкладку, Inngest — durable-цепочки внутри.
//
// Наружу уходят ТОЛЬКО эти четыре типа идентичности; `profile-updated`,
// `consent-revoked` и денежные события живут в своих модулях.
import { z } from "zod";
import {
	EmailSchema,
	IsoDateTimeSchema,
	ServiceIdSchema,
	SubjectSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

/** URI типов событий по реестрам OpenID RISC / CAEP — их ждут внешние приёмники. */
export const IDENTITY_SET_EVENT_URIS = {
	"identifier-changed": "https://schemas.openid.net/secevent/risc/event-type/identifier-changed",
	"account-disabled": "https://schemas.openid.net/secevent/risc/event-type/account-disabled",
	"account-purged": "https://schemas.openid.net/secevent/risc/event-type/account-purged",
	"session-revoked": "https://schemas.openid.net/secevent/caep/event-type/session-revoked",
} as const;

export const IdentitySetEventTypeSchema = z.enum([
	"identifier-changed",
	"account-disabled",
	"account-purged",
	"session-revoked",
]);

/** Субъект события — только `sub`. Внутренний `user_id` наружу не уходит. */
export const SetSubjectSchema = z.object({
	format: z.literal("opaque"),
	id: SubjectSchema,
});

/** Смена основного идентификатора. Инициируется только поддержкой (инвариант №2). */
export const IdentifierChangedPayloadSchema = z.object({
	subject: SetSubjectSchema,
	/** Новая почта. Прежняя не рассылается — сервисы ключуются по `sub`. */
	newValue: EmailSchema,
	changedAt: IsoDateTimeSchema,
});

/** Аккаунт отключён (бан, запрос на удаление). Данные ещё живы. */
export const AccountDisabledPayloadSchema = z.object({
	subject: SetSubjectSchema,
	reason: z.enum(["banned", "pending-deletion", "security", "support"]),
	disabledAt: IsoDateTimeSchema,
});

/** Аккаунт стёрт. `sub` после этого не переиспользуется никогда. */
export const AccountPurgedPayloadSchema = z.object({
	subject: SetSubjectSchema,
	purgedAt: IsoDateTimeSchema,
});

/** Сессии погашены — сервис обязан выкинуть свои токены, выданные по нашему логину. */
export const SessionRevokedPayloadSchema = z.object({
	subject: SetSubjectSchema,
	reason: z.enum(["user", "admin", "security", "password-reset", "deletion"]),
	revokedAt: IsoDateTimeSchema,
	/** Пусто = погашены все сессии субъекта. */
	sessionIds: z.array(UuidV7Schema).default([]),
});

/** Конверт SET (RFC 8417). `events` — карта `URI → payload`, ровно один ключ. */
export const IdentitySetEnvelopeSchema = z.object({
	jti: UuidV7Schema,
	iss: z.url(),
	aud: z.array(ServiceIdSchema).min(1),
	iat: z.number().int().positive(),
	sub: SubjectSchema,
	txn: UuidV7Schema.optional(),
	events: z.record(
		z.string(),
		z.union([
			IdentifierChangedPayloadSchema,
			AccountDisabledPayloadSchema,
			AccountPurgedPayloadSchema,
			SessionRevokedPayloadSchema,
		]),
	),
});

// ─── Socket.IO ──────────────────────────────────────────────────────────────

/**
 * Push в открытую вкладку: сессию погасили.
 *
 * Два адресата у одного факта, и они делают РАЗНОЕ:
 *   • вкладка погашенной сессии (`current: true`) — немедленно уходит на вход;
 *   • остальные вкладки того же человека (`current: false`) — только
 *     перечитывают список сессий в «Безопасности». Выкидывать их нельзя:
 *     человек сам нажал «завершить» на чужом устройстве и не должен за это
 *     вылететь из своего.
 *
 * `sessionId: null` = погашены ВСЕ сессии («Выйти везде», бан): перечислять их
 * незачем, получателю важно, что его доступ больше не действует.
 */
export const SessionRevokedSocketEventSchema = z.object({
	channel: z.literal("user"),
	event: z.literal("session:revoked"),
	payload: z.object({
		sessionId: UuidV7Schema.nullable(),
		/** true, если погашена именно та сессия, в которой открыта вкладка. */
		current: z.boolean(),
		reason: z.enum(["user", "admin", "admin_ban", "security", "deletion"]),
		revokedAt: IsoDateTimeSchema,
	}),
});

export type SessionRevokedSocketPayload = z.infer<
	typeof SessionRevokedSocketEventSchema
>["payload"];

// ─── Inngest ────────────────────────────────────────────────────────────────

/**
 * Человек заведён. Единственное Inngest-событие идентичности, которое реально
 * эмитится (`verify-email-code`, `complete-google-callback`, durable-достройка
 * регистрации в `bootstrap/deps/auth.ts`).
 *
 * Наблюдательное: подписчиков у него нет. Приветственное письмо и заявка на
 * реферальную связь уходят СВОИМИ событиями прямо из места регистрации —
 * подписчик, которого нет, однажды уже сделал «welcome-письмо» строкой в
 * комментарии (В4 аудита). Событие остаётся точкой расширения для сервисов и
 * аналитики, и его форма — часть контракта.
 */
export const IdentityUserCreatedEventSchema = z.object({
	name: z.literal("identity/user.created"),
	data: z.object({
		userId: UuidV7Schema,
		/**
		 * Ключ связи между сервисами. У импортированного человека это прежний
		 * `users.id` старого SSO (Д3), поэтому строка, а не число.
		 */
		sub: SubjectSchema,
	}),
});

// Что здесь БЫЛО и почему удалено (2026-08-21, гейт `events-contract.test.ts`).
//
// `auth/email-code.requested` — письмо с кодом durable-событием не идёт и идти
// не может: человек ждёт код на экране прямо сейчас, а не «когда-нибудь после
// ретраев». Отправка синхронная, через `notify` (журнал `outbound_messages`
// + каталог текстов), см. `mutations/request-email-code.ts`.
//
// `auth/signed-in` — не эмитировался никем. Всё, ради чего он задумывался,
// делается синхронно в `completeSignIn`: журнал безопасности, устройство,
// письмо о новом устройстве. Связь «первый вход в сервис» для `links` считает
// `links/service.activated`.
//
// `auth/set.dispatch` — дублировал живое `events/set.dispatch` под другим
// именем. Владелец рассылки SET — модуль `events`, контракт там же.
//
// Все три описывали события, которых в каталоге `inngest/client.ts` нет:
// контракт, описывающий несуществующее событие, — это не план, а ложь сервисам
// экосистемы, которые по нему пишут.
